import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import dashboardService from "../../services/dashboard";
import "./Dashboard.css";

// Donut Chart Component
const DonutChart = ({ completed = 0, ongoing = 0, unassigned = 0 }) => {
  const total = completed + ongoing + unassigned;

  if (total === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '250px' }}>
        <p style={{ color: '#999', fontSize: '14px' }}>No data available</p>
      </div>
    );
  }

  // Calculate percentages
  const completedPercent = (completed / total) * 100;
  const ongoingPercent = (ongoing / total) * 100;
  const unassignedPercent = (unassigned / total) * 100;

  // Calculate cumulative percentages for positioning
  const completedEnd = completedPercent;
  const ongoingEnd = completedEnd + ongoingPercent;

  // Colors
  const colors = {
    completed: '#22C55E',
    ongoing: '#6366F1',
    unassigned: '#F59E0B'
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <svg width="220" height="220" viewBox="0 0 220 220">
        {/* Background circle */}
        <circle
          cx="110"
          cy="110"
          r="90"
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="28"
        />

        {/* Completed segment */}
        {completed > 0 && (
          <circle
            cx="110"
            cy="110"
            r="90"
            fill="none"
            stroke={colors.completed}
            strokeWidth="28"
            strokeDasharray={`${(completedPercent / 100) * 565.48} 565.48`}
            strokeDashoffset="0"
            transform="rotate(-90 110 110)"
            strokeLinecap="round"
          />
        )}

        {/* Ongoing segment */}
        {ongoing > 0 && (
          <circle
            cx="110"
            cy="110"
            r="90"
            fill="none"
            stroke={colors.ongoing}
            strokeWidth="28"
            strokeDasharray={`${(ongoingPercent / 100) * 565.48} 565.48`}
            strokeDashoffset={`-${(completedPercent / 100) * 565.48}`}
            transform="rotate(-90 110 110)"
            strokeLinecap="round"
          />
        )}

        {/* Unassigned segment */}
        {unassigned > 0 && (
          <circle
            cx="110"
            cy="110"
            r="90"
            fill="none"
            stroke={colors.unassigned}
            strokeWidth="28"
            strokeDasharray={`${(unassignedPercent / 100) * 565.48} 565.48`}
            strokeDashoffset={`-${(ongoingEnd / 100) * 565.48}`}
            transform="rotate(-90 110 110)"
            strokeLinecap="round"
          />
        )}

        {/* Center text */}
        <text
          x="110"
          y="105"
          textAnchor="middle"
          fontSize="32"
          fontWeight="700"
          fill="#1a1a1a"
        >
          {total}
        </text>
        <text
          x="110"
          y="125"
          textAnchor="middle"
          fontSize="14"
          fill="#666"
        >
          Total Orders
        </text>
      </svg>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kpis, setKpis] = useState({
    total: 0,
    completed: 0,
    ongoing: 0,
    unassigned: 0
  });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [kpisData, recentTasks] = await Promise.all([
        dashboardService.getClientKPIs(),
        dashboardService.getRecentTasks(5)
      ]);

      setKpis(kpisData);
      setTasks(recentTasks);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = () => {
    navigate('/add-task');
  };

  const getTaskType = (status) => {
    const statusMap = {
      'Pending': 'pending',
      'Assigned': 'ongoing',
      'In Transit': 'ongoing',
      'Delivered': 'delivered',
      'Exception': 'pending',
      'Returned': 'pending'
    };
    return statusMap[status] || 'pending';
  };

  if (loading) {
    return (
      <div className="mq-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <p style={{ fontSize: '18px', color: '#6366f1' }}>Loading dashboard...</p>
      </div>
    );
  }

  const kpiArray = [
    { label: "Total Deliveries", value: kpis.total || 0, color: '#FFDB6B' },
    { label: "Completed", value: kpis.completed || 0, color: '#22C55E' },
    { label: "Ongoing", value: kpis.ongoing || 0, color: '#6366F1' },
    { label: "Unassigned", value: kpis.unassigned || 0, color: '#F59E0B' },
  ];

  return (
    <div className="mq-dashboard">
      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* TOP: Welcome + Create Job */}
      <section className="mq-dashboard-header">
        <div>
          <p className="mq-dashboard-welcome-label">Welcome Back,</p>
          <h1 className="mq-dashboard-welcome-name">{user?.firstName || 'User'} {user?.lastName || ''}</h1>
        </div>

        <button className="mq-btn mq-dashboard-create-btn" onClick={handleCreateJob}>+ Create Job</button>
      </section>

      {/* KPI CARDS */}
      <section className="mq-kpi-row">
        {kpiArray.map((kpi, index) => (
          <div className="mq-card mq-kpi" key={kpi.label}>
            <div className="mq-kpi-label">{kpi.label}</div>
            <div className="mq-kpi-value">{kpi.value}</div>
          </div>
        ))}
      </section>

      {/* BOTTOM: Overview gauge + Recent tasks */}
      <section className="mq-dashboard-bottom">
        {/* OVERVIEW / GAUGE */}
        <div className="mq-card mq-overview-card">
          <div className="mq-overview-header">
            <span className="mq-overview-title">Order Overview</span>
            <button className="mq-overview-filter" onClick={() => fetchDashboardData()}>
              Refresh <span className="mq-caret">⟳</span>
            </button>
          </div>

          <div className="mq-overview-gauge">
            <DonutChart
              completed={kpis.completed || 0}
              ongoing={kpis.ongoing || 0}
              unassigned={kpis.unassigned || 0}
            />

            <div className="mq-overview-legend">
              <div className="mq-legend-item">
                <span className="mq-legend-dot" style={{ backgroundColor: '#22C55E' }} />
                <span>Completed ({kpis.completed || 0})</span>
              </div>
              <div className="mq-legend-item">
                <span className="mq-legend-dot" style={{ backgroundColor: '#6366F1' }} />
                <span>Ongoing ({kpis.ongoing || 0})</span>
              </div>
              <div className="mq-legend-item">
                <span className="mq-legend-dot" style={{ backgroundColor: '#F59E0B' }} />
                <span>Unassigned ({kpis.unassigned || 0})</span>
              </div>
            </div>
          </div>
        </div>

        {/* RECENT TASKS */}
        <div className="mq-card mq-recent-card">
          <div className="mq-recent-header">
            <span className="mq-recent-title">Recent Tasks</span>
            <button
              className="mq-overview-filter"
              onClick={() => navigate('/tasks')}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              View All →
            </button>
          </div>

          <div className="mq-recent-list">
            {tasks.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#999' }}>
                <p style={{ margin: '0 0 10px', fontSize: '14px' }}>No recent tasks</p>
                <button
                  className="mq-btn mq-dashboard-create-btn"
                  onClick={handleCreateJob}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  + Create First Task
                </button>
              </div>
            ) : (
              tasks.map((task) => (
                <div
                  className="mq-recent-row"
                  key={task._id}
                  onClick={() => navigate('/tasks')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="mq-recent-main">
                    <div className="mq-recent-id-block">
                      <div className="mq-recent-id-label">Task ID</div>
                      <div className="mq-recent-id-value">{task.jobId}</div>
                    </div>
                    <div className="mq-recent-address">{task.to?.name || task.to?.address || 'N/A'}</div>
                  </div>

                  <div className="mq-recent-status">
                    <span
                      className={`mq-task-pill mq-task-pill-${getTaskType(task.status)}`}
                    >
                      {task.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
